import { type AuthTokenResponse, MeResponseSchema } from '@clinic/contracts';
import { type QueryClient, createQuery } from '@tanstack/svelte-query';
import { apiRequest, refreshSession } from '../api/client';
import { type Session, sessionStore } from './session-store';

/** `unreachable`: the session could not be checked (server or network down); not the same as signed out. */
export type AuthStatus = 'restoring' | 'authenticated' | 'anonymous' | 'unreachable';

class Auth {
  session = $state<Session | null>(sessionStore.get());
  private restore = $state<'pending' | 'done' | 'failed'>('pending');
  private attempt = 0;

  constructor() {
    sessionStore.subscribe(() => {
      this.session = sessionStore.get();
    });
  }

  get status(): AuthStatus {
    if (this.session) return 'authenticated';
    if (this.restore === 'pending') return 'restoring';
    return this.restore === 'failed' ? 'unreachable' : 'anonymous';
  }

  /** Restores the session from the refresh cookie (on start, and again after `unreachable`). */
  restoreSession(): void {
    this.attempt += 1;
    const attempt = this.attempt;
    this.restore = 'pending';
    refreshSession().then(
      () => {
        if (attempt === this.attempt) this.restore = 'done';
      },
      () => {
        if (attempt === this.attempt) this.restore = 'failed';
      },
    );
  }

  signIn(tokens: AuthTokenResponse): void {
    sessionStore.set({ accessToken: tokens.accessToken, user: tokens.user });
  }

  async signOut(queryClient: QueryClient): Promise<void> {
    try {
      await apiRequest('/v1/auth/logout', { method: 'POST' });
    } finally {
      sessionStore.set(null);
      queryClient.clear();
    }
  }
}

export const auth = new Auth();

export const meQueryKey = ['me'] as const;

/** The current user and their memberships (with permissions) — drives navigation and guards. */
export function createMeQuery() {
  return createQuery(() => ({
    queryKey: meQueryKey,
    queryFn: ({ signal }) => apiRequest('/v1/me', { schema: MeResponseSchema, signal }),
    enabled: auth.session !== null,
    staleTime: 30_000,
  }));
}
